import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Autocomplete ville — s'appuie sur l'API Mapbox Geocoding v5.
 *
 * - Debounce 350 ms, limite France (country=fr), seulement les
 *   `place` (ville/commune) → pas de rues ou pays.
 * - Rend la ville + coord via onChange({ name, lat, lng }).
 * - Si l'utilisateur efface le champ, onChange(null).
 *
 * Props :
 *   - value       : objet { name, lat, lng } ou null
 *   - onChange    : (val) => void
 *   - placeholder : ex. "Saint-Denis, Sevran…"
 *   - required    : affiche l'astérisque
 *   - testid      : préfix data-testid
 */
export default function CityPickerInput({
  value,
  onChange,
  placeholder = "Ex. Saint-Denis, Sevran, Nice…",
  required = false,
  testid = "city-picker",
  label = "Ta ville",
}) {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Sync externe → interne quand value change (ex. form reset).
  useEffect(() => {
    if (!value) setQuery("");
    else if (value?.name && value.name !== query) setQuery(value.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Recherche Mapbox débouncée.
  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2 || !MAPBOX_TOKEN) {
      setResults([]);
      return undefined;
    }
    // Si c'est exactement le nom choisi, pas de recherche.
    if (value && value.name === q) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url =
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
          `?access_token=${MAPBOX_TOKEN}` +
          `&country=fr&types=place,locality,neighborhood` +
          `&language=fr&limit=6&autocomplete=true`;
        const r = await fetch(url);
        const d = await r.json();
        const feats = Array.isArray(d?.features) ? d.features : [];
        setResults(
          feats.map((f) => ({
            name: f.place_name_fr || f.place_name || f.text,
            shortName: f.text,
            lat: f.center?.[1],
            lng: f.center?.[0],
          })).filter((it) => it.lat != null && it.lng != null),
        );
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const pick = (item) => {
    setQuery(item.name);
    setResults([]);
    setOpen(false);
    onChange?.({ name: item.name, lat: item.lat, lng: item.lng });
  };

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    // Si l'utilisateur modifie après avoir choisi, on invalide la value.
    if (value && value.name !== v) onChange?.(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-[11px] font-[Outfit] font-black uppercase tracking-widest text-foreground/60 mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </label>
      <div className="relative">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50 pointer-events-none"
          strokeWidth={2.4}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onQueryChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={`${testid}-input`}
          className="w-full rounded-2xl bg-white pl-9 pr-10 py-3 font-[Outfit] font-medium text-foreground placeholder:text-foreground/40 border-2 border-black outline-none focus:ring-4 focus:ring-primary/25"
          style={{ fontSize: "16px" }}
        />
        {loading && (
          <Loader2
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-foreground/50"
            strokeWidth={2.4}
          />
        )}
      </div>

      {/* Dropdown résultats */}
      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-20 max-h-[260px] overflow-y-auto rounded-2xl bg-white border-2 border-black shadow-[0_4px_0_0_#000]"
          data-testid={`${testid}-dropdown`}
        >
          {results.map((it, i) => (
            <button
              key={`${it.name}-${i}`}
              type="button"
              onClick={() => pick(it)}
              data-testid={`${testid}-item-${i}`}
              className="w-full text-left px-3 py-2.5 hover:bg-primary/10 border-b border-black/10 last:border-b-0 flex items-center gap-2"
            >
              <MapPin className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={2.5} />
              <div className="min-w-0">
                <div className="font-[Outfit] text-[13px] font-black leading-tight truncate">
                  {it.shortName}
                </div>
                <div className="text-[11px] text-foreground/60 truncate">
                  {it.name}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {value?.lat && (
        <div className="mt-1 text-[10px] font-[Outfit] font-bold text-primary">
          ✓ Position enregistrée
        </div>
      )}
    </div>
  );
}
